import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  CheckCircle2,
  Circle,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
  Video,
  XCircle,
} from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type {
  PublicApplyAttemptSummary,
  PublicApplyInterviewSummary,
  PublicApplyMeResponse,
  PublicApplyStatusResponse,
  PublicApplyTimelineEntry,
} from "@eduviaos/shared/types";
import { fetchApplyStatus } from "@/lib/api";
import { assessmentLabel } from "@/lib/assessment-label";
import { parsePublicError } from "@/lib/error-utils";
import { formatDateTime } from "@/lib/format-time";
import { PageCard } from "@/components/AppFrame";
import { ErrorState } from "@/components/ErrorState";
import { ApplyResultPanel } from "@/components/apply/ApplyResultPanel";

/**
 * Phase F4 BE-4 — parent-facing application status page.
 *
 * Reusable view (the underlying token is purpose-bound to `view_status`
 * which the BE marks as reusable). Auto-refreshes every 60s like the
 * legacy tracker so a parent who left the tab open after submitting
 * sees stage advances without manually reloading.
 *
 * Includes a "View result" affordance for any graded attempt, which
 * inlines the structured F10b result report via the same token.
 */
export function ApplyStatusSection({ token, me }: { token: string; me: PublicApplyMeResponse }) {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";
  const [showResultForAttemptId, setShowResultForAttemptId] = React.useState<string | null>(null);

  const query = useQuery({
    queryKey: ["public-apply-status", tenantSlug, token],
    queryFn: () => fetchApplyStatus(token),
    enabled: !!tenantSlug && !!token,
    refetchInterval: 60_000,
  });

  if (query.isLoading) {
    return (
      <PageCard>
        <div
          role="status"
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
        >
          <Loader2 className="text-primary h-4 w-4 animate-spin" />
          <span>Loading application status…</span>
        </div>
      </PageCard>
    );
  }

  if (query.error) {
    const parsed = parsePublicError(query.error);
    return (
      <ErrorState
        title="Couldn't load status"
        body={parsed.message}
        cta={<Button onClick={() => query.refetch()}>Try again</Button>}
      />
    );
  }

  const data = query.data;
  if (!data) return null;

  return (
    <StatusView
      data={data}
      me={me}
      onRefresh={() => query.refetch()}
      fetching={query.isFetching}
      showResultForAttemptId={showResultForAttemptId}
      onShowResult={(attemptId) =>
        setShowResultForAttemptId((prev) => (prev === attemptId ? null : attemptId))
      }
      token={token}
    />
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function StatusView({
  data,
  me,
  onRefresh,
  fetching,
  showResultForAttemptId,
  onShowResult,
  token,
}: {
  data: PublicApplyStatusResponse;
  me: PublicApplyMeResponse;
  onRefresh: () => void;
  fetching: boolean;
  showResultForAttemptId: string | null;
  onShowResult: (attemptId: string) => void;
  token: string;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0.32, 1] }}
      className="grid gap-4"
    >
      <PageCard>
        <CurrentStageBanner stage={me.stage} />

        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-foreground text-sm font-semibold tracking-tight">
              Application timeline
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={fetching}
              aria-label="Refresh status"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <Timeline entries={data.timeline} />
        </div>
      </PageCard>

      {data.interviews.length > 0 && (
        <PageCard title="Interviews" subtitle="Online interviews scheduled for this application.">
          <ul className="grid gap-2.5">
            {data.interviews.map((interview) => (
              <InterviewRow key={interview.id} interview={interview} />
            ))}
          </ul>
        </PageCard>
      )}

      {data.attempts.length > 0 && (
        <PageCard
          title="Assessment attempts"
          subtitle="Results for any assessments completed for this application."
        >
          <ul className="grid gap-2.5">
            {data.attempts.map((attempt) => (
              <AttemptRow
                key={attempt.id}
                attempt={attempt}
                expanded={showResultForAttemptId === attempt.id}
                onToggle={() => onShowResult(attempt.id)}
                token={token}
              />
            ))}
          </ul>
        </PageCard>
      )}

      <p className="text-muted-foreground text-center text-xs">
        Status auto-refreshes every minute. Last update: {formatDateTime(new Date().toISOString())}
      </p>
    </motion.section>
  );
}

function CurrentStageBanner({ stage }: { stage: PublicApplyMeResponse["stage"] }) {
  const tone =
    stage.is_terminal && stage.terminal_outcome === "admitted"
      ? "success"
      : stage.is_terminal && stage.terminal_outcome === "rejected"
        ? "danger"
        : "info";

  const Icon = tone === "success" ? CheckCircle2 : tone === "danger" ? XCircle : Clock;

  const toneClasses =
    tone === "success"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "danger"
        ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300"
        : "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300";

  return (
    <div
      className={`flex flex-col gap-2 rounded-xl border p-4 sm:flex-row sm:items-center sm:gap-4 ${toneClasses}`}
    >
      <span aria-hidden className="flex h-8 w-8 items-center justify-center">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide opacity-80">Current stage</div>
        <div className="text-base font-semibold leading-tight">{stage.label}</div>
        {stage.next_action_hint && !stage.is_terminal && (
          <div className="mt-0.5 text-xs opacity-90">{stage.next_action_hint}</div>
        )}
        {stage.is_terminal && stage.terminal_outcome && (
          <div className="mt-0.5 text-xs opacity-90">
            Final outcome: <span className="font-medium capitalize">{stage.terminal_outcome}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Timeline({ entries }: { entries: PublicApplyTimelineEntry[] }) {
  if (entries.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">No stages configured for this funnel yet.</p>
    );
  }
  const sorted = [...entries].sort((a, b) => a.order - b.order);
  return (
    <ol className="border-border/60 grid gap-3 border-l pl-5">
      {sorted.map((entry) => {
        const stateIcon = entry.is_passed ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
        ) : entry.is_current ? (
          <Clock className="text-primary h-3.5 w-3.5" />
        ) : (
          <Circle className="text-muted-foreground/50 h-3.5 w-3.5" />
        );
        return (
          <li key={entry.key} className="relative">
            <span aria-hidden className="bg-background absolute -left-[27px] top-0.5">
              {stateIcon}
            </span>
            <div
              className={`text-sm ${
                entry.is_current
                  ? "text-foreground font-semibold"
                  : entry.is_passed
                    ? "text-foreground/80 font-medium"
                    : "text-muted-foreground"
              }`}
            >
              {entry.label}
            </div>
            {entry.is_terminal && entry.terminal_outcome && (
              <div className="text-muted-foreground text-xs capitalize">
                Outcome: {entry.terminal_outcome}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function AttemptRow({
  attempt,
  expanded,
  onToggle,
  token,
}: {
  attempt: PublicApplyAttemptSummary;
  expanded: boolean;
  onToggle: () => void;
  token: string;
}) {
  const outcomeLabel = attempt.outcome ? humaniseOutcome(attempt.outcome) : null;
  const tone = outcomeTone(attempt.outcome);
  const toneClasses =
    tone === "success"
      ? "text-emerald-700 bg-emerald-500/10 border-emerald-500/30 dark:text-emerald-300"
      : tone === "danger"
        ? "text-rose-700 bg-rose-500/10 border-rose-500/30 dark:text-rose-300"
        : tone === "warning"
          ? "text-amber-700 bg-amber-500/10 border-amber-500/30 dark:text-amber-300"
          : "text-foreground/70 bg-muted border-border";

  return (
    <li className="border-border/60 bg-card/60 rounded-lg border p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="text-muted-foreground h-4 w-4 flex-shrink-0" />
        <span className="text-foreground text-sm font-medium">{assessmentLabel(attempt)}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneClasses}`}>
          {outcomeLabel ?? humaniseStatus(attempt.status)}
        </span>
        {attempt.percent_correct !== null && (
          <span className="text-muted-foreground text-xs">
            Score:{" "}
            <span className="text-foreground font-medium">
              {attempt.percent_correct.toFixed(0)}%
            </span>
          </span>
        )}
      </div>
      <div className="text-muted-foreground mt-1.5 grid gap-x-4 gap-y-0.5 text-xs sm:grid-cols-2">
        <span>Assigned: {formatDateTime(attempt.assigned_at)}</span>
        {attempt.submitted_at && <span>Submitted: {formatDateTime(attempt.submitted_at)}</span>}
        {attempt.graded_at && <span>Graded: {formatDateTime(attempt.graded_at)}</span>}
      </div>
      {attempt.has_result_report && (
        <div className="mt-3">
          <Button type="button" variant="outline" size="sm" onClick={onToggle}>
            {expanded ? "Hide result report" : "View result report"}
            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {expanded && attempt.has_result_report && (
        <div className="mt-3">
          <ApplyResultPanel token={token} />
        </div>
      )}
    </li>
  );
}

// ── Interview row (Phase F13 BE-1d) ────────────────────────────────────

function InterviewRow({ interview }: { interview: PublicApplyInterviewSummary }) {
  const { tone, label } = interviewToneAndLabel(interview.status);
  const toneClasses =
    tone === "success"
      ? "text-emerald-700 bg-emerald-500/10 border-emerald-500/30 dark:text-emerald-300"
      : tone === "danger"
        ? "text-rose-700 bg-rose-500/10 border-rose-500/30 dark:text-rose-300"
        : tone === "warning"
          ? "text-amber-700 bg-amber-500/10 border-amber-500/30 dark:text-amber-300"
          : "text-blue-700 bg-blue-500/10 border-blue-500/30 dark:text-blue-300";

  // Only the upcoming SCHEDULED state surfaces a candidate-facing
  // "Join meeting" CTA. STARTED rows are renderable but the host is
  // already in — we still show the link in case the candidate hasn't
  // joined yet. Terminal rows (ENDED / CANCELLED / ERRORED) hide
  // the join CTA so candidates don't click into a dead Zoom link.
  const canJoin = interview.status === "scheduled" || interview.status === "started";

  return (
    <li className="border-border/60 bg-card/60 rounded-lg border p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <Video className="text-muted-foreground h-4 w-4 flex-shrink-0" />
        <span className="text-foreground text-sm font-medium">{interview.topic}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneClasses}`}>
          {label}
        </span>
      </div>
      <div className="text-muted-foreground mt-1.5 grid gap-x-4 gap-y-0.5 text-xs sm:grid-cols-2">
        <span>
          When: {formatDateTime(interview.scheduled_for)}{" "}
          <span className="opacity-70">({interview.timezone})</span>
        </span>
        <span>Duration: {interview.duration_minutes} min</span>
      </div>
      {interview.agenda && (
        <p className="text-muted-foreground mt-2 whitespace-pre-line text-xs">{interview.agenda}</p>
      )}
      {canJoin && (
        <div className="mt-3">
          <Button type="button" variant="default" size="sm" asChild>
            <a href={interview.join_url} target="_blank" rel="noopener noreferrer">
              Join interview
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      )}
      {interview.status === "cancelled" && (
        <p className="text-muted-foreground mt-3 text-xs">
          This session was cancelled. Please wait for an updated time from the institute or get in
          touch if you need to follow up.
        </p>
      )}
      {interview.status === "errored" && (
        <p className="text-muted-foreground mt-3 text-xs">
          We hit an issue scheduling this session. The institute has been notified — please reach
          out if you don't hear from them shortly.
        </p>
      )}
    </li>
  );
}

function interviewToneAndLabel(status: string): {
  tone: "info" | "success" | "warning" | "danger";
  label: string;
} {
  switch (status) {
    case "scheduled":
      return { tone: "info", label: "Upcoming" };
    case "started":
      return { tone: "info", label: "In progress" };
    case "ended":
      return { tone: "success", label: "Completed" };
    case "cancelled":
      return { tone: "warning", label: "Cancelled" };
    case "errored":
      return { tone: "danger", label: "Couldn't schedule" };
    default:
      return { tone: "info", label: status };
  }
}

// ── Outcome / status microcopy ─────────────────────────────────────────

function humaniseOutcome(outcome: string): string {
  switch (outcome) {
    case "pass":
      return "Passed";
    case "fail":
      return "Did not pass";
    case "review":
      return "Under review";
    case "waitlisted":
      return "Waitlisted";
    case "conditional_pass":
      return "Conditional pass";
    case "withdrawn":
      return "Withdrawn";
    default:
      return outcome.replace(/_/g, " ");
  }
}

function humaniseStatus(status: string): string {
  switch (status) {
    case "assigned":
      return "Not started";
    case "in_progress":
      return "In progress";
    case "submitted":
      return "Awaiting grading";
    case "graded":
      return "Graded";
    case "withdrawn":
      return "Withdrawn";
    case "expired":
      return "Expired";
    default:
      return status.replace(/_/g, " ");
  }
}

function outcomeTone(outcome: string | null): "success" | "danger" | "warning" | "neutral" {
  if (!outcome) return "neutral";
  if (outcome === "pass" || outcome === "conditional_pass") return "success";
  if (outcome === "fail") return "danger";
  if (outcome === "review" || outcome === "waitlisted") return "warning";
  return "neutral";
}
